import { useTranslation } from "react-i18next";
import type { Control, FieldValues, Path } from "react-hook-form";

import { FILE_INPUT_ACCEPT } from "src/shared/api-types/file";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { CheckboxField, DatePickerField, FileInputField, TextareaField } from "../FormRhf";

type CertificateDetailsFields = FieldValues & {
  files: File[];
  completed: string;
  validUntil: string | null;
  neverExpires: boolean;
  comment: string;
};

interface CertificateDetailsFormProps<TForm extends CertificateDetailsFields> {
  control: Control<TForm, any, any>;
  neverExpires: boolean;
  showWarningIcon: boolean;
  getTooltipContent: () => string;
  courseLabel?: string;
  usersLabel?: string;
}

export function CertificateDetailsForm<TForm extends CertificateDetailsFields>({
  control,
  neverExpires,
  showWarningIcon,
  getTooltipContent,
  courseLabel,
  usersLabel,
}: CertificateDetailsFormProps<TForm>) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {courseLabel && (
        <Typography variant="h4" component="h3">
          {courseLabel}
        </Typography>
      )}
      {usersLabel && (
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mt: -3,
            mb: 2,
          }}
        >
          {usersLabel}
        </Typography>
      )}

      <FileInputField
        control={control}
        name={"files" as Path<TForm>}
        label={`${t("certificate_upload.certificate_file")}*`}
        accept={FILE_INPUT_ACCEPT}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "stretch", lg: "flex-start" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <DatePickerField
            control={control}
            name={"completed" as Path<TForm>}
            label={`${t("certificate_upload.completion_date")}*`}
            disableFuture
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <DatePickerField
              control={control}
              name={"validUntil" as Path<TForm>}
              label={t("certificate_upload.valid_until")}
              clearable
              disabled={neverExpires}
            />
          </Box>
          <Tooltip title={getTooltipContent()}>
            {showWarningIcon ? (
              <WarningAmberIcon color="warning" sx={{ cursor: "help", flexShrink: 0 }} />
            ) : (
              <InfoOutlinedIcon color="action" sx={{ cursor: "help", flexShrink: 0 }} />
            )}
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <CheckboxField
            control={control}
            name={"neverExpires" as Path<TForm>}
            label={t("certificate_never_expires")}
            sx={{ whiteSpace: "nowrap", ml: 0 }}
          />
        </Box>
      </Box>

      <TextareaField control={control} name={"comment" as Path<TForm>} label={t("certificate_upload.comment")} />
    </Box>
  );
}
